/**
 * SSE client for the agentic Staff Otto turn endpoint.
 *
 * One call = ONE reasoning turn. The dashboard server proxy
 * (`POST /api/otto/act`) mints the staff EdDSA token and streams the gateway's
 * `/agent/act` SSE back to us. We parse the typed frames and hand each to
 * `onEvent`. The caller (AgenticProvider) owns the loop: it appends any
 * `tool_call` results to the transcript and calls this again.
 *
 * Same-origin on web (`env.apiUrl===''`), absolute dashboard origin on desktop.
 * Uses a plain `fetch` reader (not the JSON `api` helper) because the response
 * is a stream. Auth is the dashboard bearer; the server does the token exchange.
 */
import { parseOttoAgentEvent, type OttoActRequest, type OttoAgentEvent } from '@xop/dashboard-shared';

import { env } from '../lib/env.js';
import { tokenStore } from '../lib/tokenStore.js';

export interface StreamOttoActHandlers {
  onEvent: (event: OttoAgentEvent) => void;
}

/**
 * Stream one agentic turn. Resolves when the stream ends (`done`/`error`/EOF or
 * an abort). Never throws for a transport/HTTP failure — it emits a synthetic
 * `error` event instead, so the caller has a single failure path.
 */
export async function streamOttoAct(
  request: OttoActRequest,
  handlers: StreamOttoActHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const token = tokenStore.getAccess();
  let res: Response;
  try {
    res = await fetch(`${env.apiUrl}/api/otto/act`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      credentials: 'include',
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (signal?.aborted) return;
    handlers.onEvent({ type: 'error', message: 'Otto is unreachable right now.' });
    return;
  }

  if (!res.ok || !res.body) {
    handlers.onEvent({
      type: 'error',
      message: res.status === 403 ? 'Otto Agent is not available for your account.' : `Otto request failed (${res.status}).`,
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        emitFrame(frame, handlers);
      }
    }
    if (buffer.trim()) emitFrame(buffer, handlers);
  } catch {
    if (!signal?.aborted) handlers.onEvent({ type: 'error', message: 'Otto’s connection dropped.' });
  } finally {
    reader.releaseLock?.();
  }
}

/** Parse the `data:` payload(s) of one SSE frame and dispatch valid events. */
function emitFrame(frame: string, handlers: StreamOttoActHandlers): void {
  for (const line of frame.split('\n')) {
    const trimmed = line.trimStart();
    if (!trimmed.startsWith('data:')) continue;
    const json = trimmed.slice(5).trim();
    if (!json) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      continue;
    }
    const event = parseOttoAgentEvent(parsed);
    if (event) handlers.onEvent(event);
  }
}
